'use client';

import { useEffect, useRef, useState } from 'react';
import { Avatar, type AvatarColor } from '@/components/ui/avatar';
import { StatusPill, type MemberStatus } from '@/components/ui/pills';
import { fcfa } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Données de vitrine — aucun lien avec la base, c'est une illustration. */
const SHOWCASE: { name: string; color: AvatarColor; status: MemberStatus }[] = [
  { name: 'Awa Koné', color: 'br', status: 'paye' },
  { name: 'Mariam Traoré', color: 'gd', status: 'paye' },
  { name: 'Fatou Diallo', color: 'pl', status: 'paye' },
  { name: 'Adjoua Kouassi', color: 'rd', status: 'retard' },
];

const CAISSE_TARGET = 1_875_000;
const PAYE_TARGET = 18;
const PAYE_TOTAL = 25;
const RETARD_TARGET = 4;
const ROW_STAGGER_MS = 260;

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** Compte de 0 à `target` en `durationMs`, ease-out cubique. Retourne
 *  `target` tel quel sans `start`, ou en mouvement réduit. */
function useCountUp(target: number, durationMs: number, start: boolean, reduced: boolean): number {
  const [value, setValue] = useState(reduced || !start ? target : 0);
  const t0 = useRef<number | null>(null);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    if (!start) return;
    t0.current = null;
    let raf = 0;
    const tick = (now: number) => {
      if (t0.current === null) t0.current = now;
      const progress = Math.min(1, (now - t0.current) / durationMs);
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, durationMs, reduced]);

  return value;
}

/**
 * Le téléphone du Hero : un aperçu du tableau de bord de la gérante, animé
 * comme un mini-tutoriel plutôt qu'affiché figé — la caisse et les compteurs
 * comptent jusqu'à leur valeur, les cotisations apparaissent une à une. Sans
 * vraie démo publique (voir routes.ts), c'est la façon la plus honnête de
 * montrer le produit "en situation" : rien n'est simulé comme un vrai appel
 * réseau, tout est marqué `aria-hidden` (illustration, pas des données).
 */
export function HeroPhone() {
  const reducedMotion = useReducedMotion();
  const [started, setStarted] = useState(false);

  useEffect(() => {
    // Léger différé : la séquence démarre une fois l'entrée du parent
    // (tmpop, dans hero.tsx) quasiment terminée, pas en même temps qu'elle.
    const t = setTimeout(() => setStarted(true), reducedMotion ? 0 : 350);
    return () => clearTimeout(t);
  }, [reducedMotion]);

  const caisse = useCountUp(CAISSE_TARGET, 1200, started, reducedMotion);
  const paye = useCountUp(PAYE_TARGET, 900, started, reducedMotion);
  const retard = useCountUp(RETARD_TARGET, 900, started, reducedMotion);

  const [revealedRows, setRevealedRows] = useState(reducedMotion ? SHOWCASE.length : 0);

  useEffect(() => {
    if (reducedMotion || !started || revealedRows >= SHOWCASE.length) return;
    const t = setTimeout(() => setRevealedRows((n) => n + 1), ROW_STAGGER_MS);
    return () => clearTimeout(t);
  }, [started, revealedRows, reducedMotion]);

  return (
    <div
      // largeur fluide, plafonnée à 290px : fixe, elle débordait de 18px sur
      // les téléphones à 320px de large (galerie de repli, vieux Android).
      // `animate-tmfloat` est sur CET élément, pas le wrapper `tmpop` du
      // parent (hero.tsx) : les deux animations s'appliquent à des boîtes
      // différentes et se composent sans conflit — l'entrée joue une fois,
      // le flottement tourne en continu ensuite.
      className="w-full max-w-[290px] animate-tmfloat rounded-[38px] border border-[#1c355c] bg-navy-d p-[11px]"
      style={{ boxShadow: '0 40px 80px -30px rgba(11,37,71,.55)' }}
      aria-hidden
    >
      <div className="overflow-hidden rounded-[29px] bg-cream">
        <div className="bg-navy px-[18px] pt-5 pb-[22px] text-white">
          <div className="mb-4 flex items-center justify-between text-[12px] opacity-70">
            <span>Tontine des Braves</span>
            <span>Sem. 12/25</span>
          </div>
          <div className="text-[13px] opacity-75">Caisse actuelle</div>
          <div className="font-num text-[30px] font-bold tracking-[-0.5px]">{fcfa(caisse)}</div>
          <div className="mt-[14px] flex gap-2">
            <div className="flex-1 rounded-[10px] bg-white/10 px-2.5 py-2">
              <div className="text-[10.5px] opacity-70">Payé</div>
              <div className="font-num text-[15px] font-semibold text-[#8fe0ab]">
                {paye}/{PAYE_TOTAL}
              </div>
            </div>
            <div className="flex-1 rounded-[10px] bg-white/10 px-2.5 py-2">
              <div className="text-[10.5px] opacity-70">Retard</div>
              <div className="font-num text-[15px] font-semibold text-[#f0b86e]">{retard}</div>
            </div>
          </div>
        </div>

        <div className="px-[14px] pt-[14px] pb-[18px]">
          <div className="mb-2.5 text-[12.5px] font-semibold text-mut">
            Cotisations · cette semaine
          </div>
          {SHOWCASE.map((m, i) => (
            <div
              key={m.name}
              className={cn(
                'flex items-center gap-2.5 border-b border-line py-2 transition-all duration-300 ease-out',
                i < revealedRows ? 'translate-y-0 opacity-100' : 'translate-y-1.5 opacity-0',
              )}
            >
              <Avatar name={m.name} size={32} color={m.color} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold text-ink">{m.name}</div>
                <div className="text-[11.5px] text-mut">{fcfa(25000)}</div>
              </div>
              <StatusPill status={m.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
